const IME_PLACEHOLDER_KEYS = new Set(['Process', 'Unidentified', 'Dead']);

const CODE_BY_KEY: Record<string, string> = {
  '/': 'Slash',
  a: 'KeyA',
  d: 'KeyD',
  f: 'KeyF',
  g: 'KeyG',
  h: 'KeyH',
  j: 'KeyJ',
  k: 'KeyK',
  l: 'KeyL',
  m: 'KeyM',
  n: 'KeyN',
  o: 'KeyO',
  u: 'KeyU',
  y: 'KeyY',
};

export function shortcutKeyMatches(
  event: Pick<KeyboardEvent, 'key' | 'code'>,
  key: string,
): boolean {
  if (event.key === key) return true;
  return IME_PLACEHOLDER_KEYS.has(event.key) && event.code === CODE_BY_KEY[key];
}

export type PublicStoryShortcutAction =
  | 'focus_next'
  | 'focus_previous'
  | 'toggle_details'
  | 'open_source';

export function publicStoryShortcutAction(
  event: Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey'>,
  isTyping: boolean,
): PublicStoryShortcutAction | null {
  if (isTyping || event.metaKey || event.ctrlKey || event.altKey) return null;
  if (shortcutKeyMatches(event, 'j')) return 'focus_next';
  if (shortcutKeyMatches(event, 'k')) return 'focus_previous';
  if (shortcutKeyMatches(event, 'o')) return 'toggle_details';
  if (shortcutKeyMatches(event, 'l')) return 'open_source';
  if (event.key === 'Enter') return 'open_source';
  return null;
}

export type SentenceShortcutAction = 'focus_next' | 'focus_previous';

/** Up and down navigation shortcuts for sentence.tldr.fm (j/k and down/up arrows). */
export function sentenceShortcutAction(
  event: Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>,
  isTyping: boolean,
): SentenceShortcutAction | null {
  if (isTyping || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return null;
  if (shortcutKeyMatches(event, 'j') || event.key === 'ArrowDown') return 'focus_next';
  if (shortcutKeyMatches(event, 'k') || event.key === 'ArrowUp') return 'focus_previous';
  return null;
}


/** All article-list shortcuts, including aliases reserved for non-admins. */
export function storyShortcutAction(
  event: Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey'>,
  isTyping: boolean,
  isAdminUser: boolean,
): PublicStoryShortcutAction | 'copy_article' | 'expand_details' | 'collapse_details' | null {
  if (isTyping || event.metaKey || event.ctrlKey || event.altKey) return null;
  const publicAction = publicStoryShortcutAction(event, false);
  if (publicAction) return publicAction;
  if (shortcutKeyMatches(event, 'y')) return 'copy_article';
  if (!isAdminUser && shortcutKeyMatches(event, 'a')) return 'toggle_details';
  if (event.key === 'ArrowRight') return 'expand_details';
  if (event.key === 'ArrowLeft') return 'collapse_details';
  return null;
}

export function isSearchSubmitShortcut(
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey'>,
): boolean {
  return event.key === 'Enter' && Boolean(event.metaKey || event.ctrlKey);
}

type BareKeyEvent = Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey'>;

/** Bare `f` opens the feed switcher, unless the reader is typing somewhere. */
export function isFeedSwitcherShortcut(event: BareKeyEvent, isTyping: boolean): boolean {
  if (isTyping || event.metaKey || event.ctrlKey || event.altKey) return false;
  return shortcutKeyMatches(event, 'f');
}

/** Bare `/` focuses the search box, unless the reader is typing somewhere. */
export function isSearchFocusShortcut(event: BareKeyEvent, isTyping: boolean): boolean {
  if (isTyping || event.metaKey || event.ctrlKey || event.altKey) return false;
  return shortcutKeyMatches(event, '/');
}

/** Bare `?` toggles the shortcuts overlay, unless the reader is typing somewhere. */
export function isShortcutsHelpShortcut(event: BareKeyEvent, isTyping: boolean): boolean {
  if (isTyping || event.metaKey || event.ctrlKey || event.altKey) return false;
  return event.key === '?';
}
